/* eslint-disable @typescript-eslint/no-explicit-any */
import Table from '@/shared/components/Table/Table'
import {
  FILTER_KEYS,
  TABLE_KEYS
} from '@/shared/components/Table/models/Filter.model'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTableFilterStore } from '@/shared/components/Table/store/useTableFilterStore'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { useDishFormConfig } from '@/features/menu-management/Dishes/hooks/useDishFormConfig'
import { useDishesQuery } from '@/features/menu-management/Dishes/hooks/useDishesQuery'
import { useDishCategoriesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useDishCategoriesQuery'
import { useDishSizesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useDishSizesQuery'
import { useMenuTypesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useMenuTypesQuery'
import { useDishesColumnsConfig } from '@/features/menu-management/Dishes/hooks/useDishesColumnsConfig'
import { useSelectedLanguagesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useSelectedLanguagesQuery'
import DetailsModal from '@/shared/components/Modal/DetailsModal/DetailsModal'
import { useSpiceLevelsQuery } from '@/features/menu-management/Dishes/hooks/useSpiceLevelsQuery'
import { dishPayloadModel } from '@/features/menu-management/Dishes/api/DishesAPI.model'
import { useIngredientsQuery } from '@/features/menu-management/Ingredients/hooks/useIngredientsQuery'
import { useToppingsQuery } from '@/features/menu-management/Toppings/hooks/useToppingsQuery'
import styles from '@/features/menu-management/Dishes/DishesManagement.module.scss'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import { useImageSlots } from '@/shared/store/useImageSlots'
import IMAGE_SLOTS_KEYS from '@/shared/store/imageSlots.model'
import {
  formatPrice,
  formatPriceForInput,
  majorToMinor
} from '@/shared/utils/price'
import { translatedName } from '@/shared/utils/translatedName'

function DishesManagement() {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [dishId, setDishId] = useState<string>()
  const configuredDishIdRef = useRef<string>()
  // The submit now awaits the POST and the image upload, so the modal stays
  // open long enough for a second click to create a second dish. It used to
  // close synchronously.
  const isSubmittingRef = useRef(false)
  const {
    dishesList,
    dish,
    dishImage,
    deleteDishes,
    postDishAsync,
    putDish,
    postDishImageAsync,
    dishesListLoading
  } = useDishesQuery(dishId)
  const clearImageSlot = useImageSlots(s => s.clear)

  const { dishCategoriesList, dishCategoriesOptions } = useDishCategoriesQuery()
  const { dishSizesList, dishSizesOptions } = useDishSizesQuery()
  const { menuTypesList, menuTypesOptions } = useMenuTypesQuery()
  const { spiceLevelIdByLevel, levelBySpiceLevelId } = useSpiceLevelsQuery()

  /*
    Held in a ref, not read straight from the hook.

    `mapFormDataToDishPayload` is re-created on every render, and the effect that
    freezes the edit form's `onSubmit` calls it. Referencing a component-scope
    function inside the builder makes the builder reactive, which drags it into
    that effect's dependencies and re-runs the effect — and rebuilds the form
    config — on every render. Through a ref the builder stays static and still
    reads the current dictionary at submit time.
  */
  const spiceLevelIdByLevelRef = useRef(spiceLevelIdByLevel)
  useEffect(() => {
    spiceLevelIdByLevelRef.current = spiceLevelIdByLevel
  }, [spiceLevelIdByLevel])

  const { ingredientsList, unitsList } = useIngredientsQuery()
  const { toppingsList } = useToppingsQuery()
  const resetIsDirty = useFormDirtyStore(s => s.reset)

  useEffect(() => resetIsDirty(), [])

  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { defaultLanguageOption, selectedLanguagesList } =
    useSelectedLanguagesQuery(currentLanguage)

  const { filters } = useTableFilterStore()

  const defaultValues = useMemo(
    () => ({
      dishLanguage: defaultLanguageOption as OptionType,
      dishName: selectedLanguagesList.reduce(
        (acc, lang) => {
          acc[lang.languageCode] = ''
          return acc
        },
        {} as Record<string, string>
      ),
      dishDescription: selectedLanguagesList.reduce(
        (acc, lang) => {
          acc[lang.languageCode] = ''
          return acc
        },
        {} as Record<string, string>
      ),
      dishImage: '',
      dishCategories: [],
      dishToppings: [],
      dishNumber: '',
      menuTypes: [],
      dishSizes: [],
      dishPrice: '',
      dishTax: '',
      dishSize: {} as OptionType,
      priceByDishSize: ''
    }),
    [defaultLanguageOption, selectedLanguagesList]
  )

  const initialFormConfig = useDishFormConfig(dishId)

  const [formConfig, setFormConfig] = useState(initialFormConfig)

  const filterLanguageCode =
    filters.dishes?.[FILTER_KEYS.LANGUANGE_FILTER] || defaultLanguageOption
  const selectedCategoryLabels = (
    filters.dishes?.[FILTER_KEYS.CATEGORIES_FILTER] || []
  ).map(filter => filter.label)
  const selectedSizeLabels = (
    filters.dishes?.[FILTER_KEYS.DISH_SIZE_FILTER] || []
  ).map(filter => filter.label)
  const selectedMenuTypeLabels = (
    filters.dishes?.[FILTER_KEYS.MENU_TYPE_FILTER] || []
  ).map(filter => filter.label)
  const selectedCategoryLabelsSet = new Set(selectedCategoryLabels)
  const selectedSizeLabelsSet = new Set(selectedSizeLabels)
  const selectedMenuTypeLabelsSet = new Set(selectedMenuTypeLabels)

  const mappedDishesList = dishesList
    ?.map(dish => {
      const findTranslation = (translations: any[], lang: string) =>
        translations.find(t => t.languageCode === lang)?.name || ''

      const findDescription = (translations: any[], lang: string) =>
        translations.find(t => t.languageCode === lang)?.description || ''

      const priceToDishSizeIds =
        dish.priceToDishSizeId?.map(priceToDishSize => {
          const sizeName =
            dishSizesList.data
              ?.find(s => s.id === priceToDishSize.id)
              ?.translations.find(
                tr => filterLanguageCode?.value === tr.languageCode
              )?.name || ''

          return `${sizeName}: ${formatPrice(priceToDishSize.price)}`
        }) ?? []

      const ingredientsByDishSizeId =
        Array.isArray(dish.ingredientsByDishSizeId) &&
        dish.ingredientsByDishSizeId.length > 0
          ? dish.ingredientsByDishSizeId.flatMap(sizeEntry => {
              const sizeName =
                dishSizesList.data
                  ?.find(size => size.id === sizeEntry.id)
                  ?.translations.find(
                    tr => tr.languageCode === filterLanguageCode?.value
                  )?.name || ''

              return sizeEntry.ingredients.map(ingredient => {
                const ingredientName =
                  ingredientsList
                    ?.find(i => i.id === ingredient.id)
                    ?.translations.find(
                      tr => tr.languageCode === filterLanguageCode?.value
                    )?.name || ''

                const unitName =
                  unitsList.data
                    ?.find(u => u.id === ingredient.unit.id)
                    ?.translations.find(
                      tr => tr.languageCode === filterLanguageCode?.value
                    )?.name || ''

                return `${sizeName} → ${ingredientName} (${
                  ingredient.unit.quantity
                }${unitName || ''})`
              })
            })
          : []

      const mappedIngredients = (dish.ingredients ?? [])
        .map(ingredient => {
          const match = ingredientsList?.find(i => i.id === ingredient?.id)

          return (
            match?.translations.find(
              tr => tr.languageCode === filterLanguageCode?.value
            )?.name ||
            match?.translations?.[0]?.name ||
            ''
          )
        })
        .filter(Boolean)

      return {
        id: dish.id,
        // The list response carries the picture; this column was hardcoded
        // empty, so an uploaded photo never showed up in the table. `?t=` is
        // the version, without which a replaced photo keeps serving from cache.
        imageUrl: dish.image?.url
          ? `${dish.image.url}?t=${dish.image.version}&alt=media`
          : '',
        name: findTranslation(
          dish.translations,
          filterLanguageCode?.value || 'en'
        ),
        description: findDescription(
          dish.translations,
          filterLanguageCode?.value || 'en'
        ),
        price: (() => {
          if (priceToDishSizeIds.length) {
            return [
              {
                id: dish.id || '',
                label: t('TABLE.MENU.CELLS.PRICE_VARIES_BY_SIZE'),
                tooltipContent: priceToDishSizeIds
              }
            ]
          }
          if (dish.price) {
            return formatPrice(dish.price)
          }
          return null
        })(),
        menuNumber: dish.menuNumber,
        ingredients: (() => {
          if (ingredientsByDishSizeId.length) {
            return [
              {
                id: dish.id,
                label: t('TABLE.MENU.CELLS.INGREDIENTS_BY_SIZE'),
                tooltipContent: ingredientsByDishSizeId
              }
            ]
          }
          if (mappedIngredients.length) {
            return [
              {
                id: dish.id,
                label: t('TABLE.MENU.HEADERS.INGREDIENTS'),
                tooltipContent: mappedIngredients
              }
            ]
          }
          return []
        })(),
        categories:
          dishCategoriesList.data
            ?.filter(c => dish.dishCategoryIds.includes(c.id ?? ''))
            .map(c => ({
              id: c.id,
              label:
                c.translations.find(
                  tr => filterLanguageCode?.value === tr.languageCode
                )?.name || ''
            })) || [],

        sizes:
          dishSizesList.data
            ?.filter(s => dish.dishSizeIds.includes(s.id ?? ''))
            .map(s => ({
              id: s.id,
              label:
                s.translations.find(
                  tr => filterLanguageCode?.value === tr.languageCode
                )?.name || ''
            })) || [],

        menuTypes:
          menuTypesList.data
            ?.filter(m => dish.dishMenuTypeIds.includes(m.id ?? ''))
            .map(m => ({
              id: m.id,
              label:
                m.translations.find(
                  tr => filterLanguageCode?.value === tr.languageCode
                )?.name || ''
            })) || [],

        toppings:
          toppingsList
            ?.filter(tp => dish.dishToppingIds.includes(tp.id ?? ''))
            .map(tp => ({
              id: tp.id,
              label:
                tp.translations.find(
                  tr => filterLanguageCode?.value === tr.languageCode
                )?.name || ''
            })) || []
      }
    })
    .filter(dish => {
      const rowCategoryLabels = Array.isArray(dish.categories)
        ? dish.categories.map(category => category.label)
        : []
      const rowSizeLabels = Array.isArray(dish.sizes)
        ? dish.sizes.map(size => size.label)
        : []
      const rowMenuTypeLabels = Array.isArray(dish.menuTypes)
        ? dish.menuTypes.map(menuType => menuType.label)
        : []

      const categoriesMatch =
        selectedCategoryLabels.length === 0 ||
        rowCategoryLabels.some(label => selectedCategoryLabelsSet.has(label))
      const sizesMatch =
        selectedSizeLabels.length === 0 ||
        rowSizeLabels.some(label => selectedSizeLabelsSet.has(label))
      const menuTypesMatch =
        selectedMenuTypeLabels.length === 0 ||
        rowMenuTypeLabels.some(label => selectedMenuTypeLabelsSet.has(label))

      return categoriesMatch && sizesMatch && menuTypesMatch
    })

  const mapFormDataToDishPayload = (formData: any): dishPayloadModel => {
    const priceToDishSizeId = (formData.dishPriceBySizeList || [])
      .filter(
        (row: any) => row.priceBySize !== undefined && row.priceBySize !== ''
      )
      .map((row: any) => ({
        id: row.id,
        price: majorToMinor(row.priceBySize) ?? 0
      }))

    // Helper: resolve ingredient UUID from a row.
    // Prefer the stored storedIngredientId (set during edit pre-fill),
    // fall back to label matching for newly-created rows.
    const resolveIngredientId = (row: any): string | undefined =>
      row.storedIngredientId ||
      formData.dishIngredients?.find(
        (i: any) => i.label === row.dishIngredients
      )?.value

    // Helper: resolve size UUID from a row.
    const resolveSizeId = (row: any): string | undefined =>
      row.storedSizeId ||
      formData.dishSizes?.find((s: any) => s.label === row.dishSizes)?.value

    const ingredientsByDishSizeId =
      formData.ingredientsByDishSizeId?.reduce((acc: any[], row: any) => {
        const ingredientId = resolveIngredientId(row)
        const sizeId = resolveSizeId(row)

        if (!ingredientId || !sizeId) return acc

        let entry = acc.find((e: any) => e.id === sizeId)
        if (!entry) {
          entry = { id: sizeId, ingredients: [] }
          acc.push(entry)
        }

        entry.ingredients.push({
          id: ingredientId,
          unit: {
            id: row.unit,
            quantity: Number(row.quantity) || 0
          }
        })

        return acc
      }, [] as any[]) || []

    const hasMatrixData = ingredientsByDishSizeId.length > 0

    // Build flat ingredients as DishIngredientDto objects ({id, unit: {id, quantity}})
    // For dishes without sizes, the table rows won't have a sizeId and are skipped
    // by the reduce above, so we handle them separately here.
    const flatIngredients = hasMatrixData
      ? []
      : (formData.ingredientsByDishSizeId || [])
          .map((row: any) => {
            const ingredientId = resolveIngredientId(row)
            if (!ingredientId) return null
            return {
              id: ingredientId,
              unit: {
                id: row.unit || '',
                quantity: Number(row.quantity) || 0
              }
            }
          })
          .filter(Boolean)

    const translations = Object.keys(formData.dishName || {}).map(langCode => ({
      languageCode: langCode,
      name: formData.dishName?.[langCode],
      description: formData.dishDescription?.[langCode]
    }))

    const dishPayload: dishPayloadModel = {
      price: majorToMinor(formData.dishPrice),
      taxRate:
        formData.dishTax === '' || formData.dishTax === undefined
          ? undefined
          : Number(formData.dishTax),
      priceToDishSizeId,
      ingredients: flatIngredients,
      ingredientsByDishSizeId,
      menuNumber: Number(formData.dishNumber),
      translations,
      dishToppingIds: formData.dishToppings?.map((t: any) => t.value) || [],
      dishMenuTypeIds: formData.menuTypes?.map((m: any) => m.value) || [],
      dishCategoryIds: formData.dishCategories?.map((c: any) => c.value) || [],
      dishSizeIds: formData.dishSizes?.map((s: any) => s.value) || [],
      allergenIds: [],
      dietId: formData.dietTypes?.value ?? null,
      /*
        The rating hands over 1-3; the dish stores the spice level's id. Matched
        on the backend's own `level` column rather than on the list's position,
        because `getAll` has no ORDER BY.
      */
      spiceLevelId: spiceLevelIdByLevelRef.current(formData.spiceLevel)
    }

    return dishPayload
  }

  // The picture lives in an image slot, not in the form value: the cropper
  // writes a blob there and the dish payload has no field for it. Read at
  // submit time rather than from a subscribed value, because the edit form's
  // `onSubmit` is built inside an effect that does not re-run when the user
  // crops a new picture.
  const uploadDishImageFromSlot = useCallback(
    async (targetDishId: string) => {
      const slot = useImageSlots.getState().slots[IMAGE_SLOTS_KEYS.DISH_IMAGE]
      const blob = slot?.croppedBlob
      if (!blob) return
      const file = new File([blob], 'dish-image', {
        type: slot?.mime || 'image/webp'
      })
      await postDishImageAsync({ file, dishId: targetDishId }).catch(
        () => undefined
      )
    },
    [postDishImageAsync]
  )

  const handleEditDish = (id: string) => {
    setDishId(id)
  }

  const handleOnDeleteItem = (id: string) => {
    deleteDishes([id])
  }

  const columns = useDishesColumnsConfig(handleEditDish, handleOnDeleteItem)

  const handleOnDeleteItems = (ids: string[]) => {
    deleteDishes(ids)
  }

  // Also the end of a successful save. Leaving `dishId` and the ref pointing at
  // the dish just edited made a second edit of that same row a no-op: the
  // effect below only reconfigures the form when the id actually changes.
  const handleOnCloseModal = useCallback(() => {
    // Abandoning the form must drop the cropped picture too, or it is still in
    // the slot when the next dish is opened.
    clearImageSlot(IMAGE_SLOTS_KEYS.DISH_IMAGE)
    configuredDishIdRef.current = undefined
    setDishId(undefined)
    setIsModalOpen(false)
  }, [clearImageSlot])

  const handleAddDish = () => {
    const onSubmit = async (data: any) => {
      if (isSubmittingRef.current) return
      isSubmittingRef.current = true
      try {
        const dishPayload = mapFormDataToDishPayload(data)
        // POST answers with the new dish's id, and the image endpoint is keyed
        // by it, so the upload has to wait for the dish to exist.
        const created = await postDishAsync(dishPayload)
        if (created?.id) await uploadDishImageFromSlot(created.id)
        resetIsDirty()
        handleOnCloseModal()
      } finally {
        isSubmittingRef.current = false
      }
    }

    setFormConfig({ ...initialFormConfig, defaultValues, onSubmit })
    setIsModalOpen(true)
  }

  useEffect(() => {
    // Waits for the image query too: `initialFormConfig` carries the dish-image
    // query result and the whole config is frozen into state here, so a snapshot
    // taken while the image is still in flight keeps `data: undefined` for as
    // long as the modal stays open, the saved photo would simply not appear.
    if (
      dish.data &&
      dishId &&
      !dishImage.isLoading &&
      dishId !== configuredDishIdRef.current
    ) {
      configuredDishIdRef.current = dishId

      const onSubmit = async (data: any) => {
        if (isSubmittingRef.current) return
        isSubmittingRef.current = true
        try {
          const dishPayload = mapFormDataToDishPayload(data)
          putDish({
            dishPayload,
            dishId
          })
          await uploadDishImageFromSlot(dishId)
          resetIsDirty()
          handleOnCloseModal()
        } finally {
          isSubmittingRef.current = false
        }
      }

      const defaultDishNames = dish.data.translations.reduce(
        (acc, item) => {
          acc[item.languageCode] = item.name
          return acc
        },
        {} as Record<string, string>
      )

      const defaultDishDescriptions = dish.data.translations.reduce(
        (acc, item) => {
          acc[item.languageCode] = item.description || ''
          return acc
        },
        {} as Record<string, string>
      )

      const defaultDishCategories = dishCategoriesOptions.filter(c =>
        dish.data?.dishCategoryIds.includes(c.value)
      )

      const defaultDishSizes = dishSizesOptions.filter(s =>
        dish.data?.dishSizeIds.includes(s.value)
      )

      const defaultMenuTypes = menuTypesOptions.filter(m =>
        dish.data?.dishMenuTypeIds.includes(m.value)
      )

      // Build dishIngredients multi-select options from both flat ingredients
      // and ingredientsByDishSizeId (for size-specific dishes, flat may be empty)
      const ingredientIdsFromFlat = (dish.data.ingredients ?? []).map(
        (ingredient: any) => ingredient.id ?? ingredient
      )
      const ingredientIdsFromMatrix = Array.isArray(
        dish.data.ingredientsByDishSizeId
      )
        ? dish.data.ingredientsByDishSizeId.flatMap((sizeEntry: any) =>
            sizeEntry.ingredients.map((ing: any) => ing.id)
          )
        : []
      const uniqueIngredientIds = [
        ...new Set([...ingredientIdsFromFlat, ...ingredientIdsFromMatrix])
      ]
      const defaultDishIngredients = uniqueIngredientIds.map(
        (ingredientId: any) => {
          const match = ingredientsList?.find(i => i.id === ingredientId)
          return {
            value: ingredientId,
            label:
              match?.translations?.find(
                tr => tr.languageCode === (filterLanguageCode?.value || 'en')
              )?.name ||
              match?.translations?.[0]?.name ||
              ingredientId
          }
        }
      )

      const defaultDishToppings = (dish.data.dishToppingIds ?? []).map(id => {
        const match = toppingsList?.find(topping => topping.id === id)
        return {
          value: id,
          label:
            match?.translations?.find(
              tr => tr.languageCode === (filterLanguageCode?.value || 'en')
            )?.name ||
            match?.translations?.[0]?.name ||
            id
        }
      })

      // Build ingredient table rows (step 3): ingredient × size matrix
      const lang = filterLanguageCode?.value || 'en'
      const defaultIngredientRows: any[] = []

      if (
        Array.isArray(dish.data.ingredientsByDishSizeId) &&
        dish.data.ingredientsByDishSizeId.length > 0
      ) {
        // Has size-specific ingredients
        dish.data.ingredientsByDishSizeId.forEach((sizeEntry: any) => {
          const sizeMatch = dishSizesList.data?.find(s => s.id === sizeEntry.id)
          const sizeName =
            sizeMatch?.translations?.find(tr => tr.languageCode === lang)
              ?.name || sizeEntry.id

          sizeEntry.ingredients.forEach((ingredient: any) => {
            const ingredientMatch = ingredientsList?.find(
              i => i.id === ingredient.id
            )
            const ingredientName = translatedName(
              ingredientMatch?.translations,
              lang,
              defaultLanguageOption?.value as string | undefined
            )

            defaultIngredientRows.push({
              id: `${ingredient.id}-${sizeEntry.id}`,
              dishIngredients: ingredientName,
              dishSizes: sizeName,
              storedIngredientId: ingredient.id,
              storedSizeId: sizeEntry.id,
              quantity: String(ingredient.unit?.quantity ?? ''),
              unit: ingredient.unit?.id ?? ''
            })
          })
        })
      } else if (Array.isArray(dish.data.ingredients)) {
        // Flat ingredients (no sizes)
        dish.data.ingredients.forEach((ingredient: any) => {
          const ingredientId = ingredient.id ?? ingredient
          const ingredientMatch = ingredientsList?.find(
            i => i.id === ingredientId
          )
          const ingredientName =
            ingredientMatch?.translations?.find(tr => tr.languageCode === lang)
              ?.name || ingredientId

          defaultIngredientRows.push({
            id: ingredientId,
            dishIngredients: ingredientName,
            storedIngredientId: ingredientId,
            quantity: String(ingredient.unit?.quantity ?? ''),
            unit: ingredient.unit?.id ?? ''
          })
        })
      }

      // Build price-by-size rows (step 4)
      const defaultPriceBySizeRows = (dish.data.priceToDishSizeId ?? []).map(
        (entry: any) => {
          const sizeMatch = dishSizesList.data?.find(s => s.id === entry.id)
          const sizeName = translatedName(
            sizeMatch?.translations,
            lang,
            defaultLanguageOption?.value as string | undefined
          )
          return {
            id: entry.id,
            dishSizes: sizeName,
            priceBySize: formatPriceForInput(entry.price)
          }
        }
      )

      const newDefaultValues = {
        dishLanguage: defaultLanguageOption,
        selectedLanguages: defaultLanguageOption,
        dishName: defaultDishNames,
        dishDescription: defaultDishDescriptions,
        dishCategories: defaultDishCategories,
        dishSizes: defaultDishSizes,
        menuTypes: defaultMenuTypes,
        dishIngredients: defaultDishIngredients,
        dishToppings: defaultDishToppings,
        dishNumber: dish.data.menuNumber ?? '',
        dishPrice: formatPriceForInput(dish.data.price),
        dishTax: dish.data.taxRate ?? '',
        dishImage: '',
        ingredientsByDishSizeId: defaultIngredientRows,
        dishPriceBySizeList: defaultPriceBySizeRows,
        // Without this the rating opened empty on every edit, and saving wrote
        // that emptiness back over whatever the dish had.
        spiceLevel: levelBySpiceLevelId(dish.data.spiceLevelId)
      }

      setFormConfig({
        ...initialFormConfig,
        defaultValues: newDefaultValues,
        onSubmit
      })
      setIsModalOpen(true)
    }
  }, [
    dish.data,
    dishId,
    dishesList,
    ingredientsList,
    toppingsList,
    putDish,
    initialFormConfig,
    defaultLanguageOption,
    dishCategoriesOptions,
    dishSizesOptions,
    dishSizesList.data,
    menuTypesOptions,
    filterLanguageCode,
    resetIsDirty,
    handleOnCloseModal,
    uploadDishImageFromSlot,
    dishImage.isLoading,
    levelBySpiceLevelId
  ])

  const columnsModel = [
    'imageUrl',
    'name',
    'description',
    'sizes',
    'price',
    'menuTypes',
    'ingredients',
    'toppings'
  ]

  const filtersList = [
    FILTER_KEYS.LANGUANGE_FILTER,
    FILTER_KEYS.CATEGORIES_FILTER,
    FILTER_KEYS.DISH_SIZE_FILTER,
    FILTER_KEYS.MENU_TYPE_FILTER
  ]

  return (
    <div className={styles.dishesTableWrapper}>
      <Table
        columns={columns}
        columnsModel={columnsModel}
        filtersList={filtersList}
        tableKey={TABLE_KEYS.DISHES}
        initialRows={mappedDishesList || []}
        filterModel={{ items: [] }}
        handleOnDeleteItems={handleOnDeleteItems}
        handleOnAddItem={handleAddDish}
        isLoading={dishesListLoading}
      />
      <DetailsModal
        title={t('DISH_DETAILS_MODAL.TITLE')}
        formConfig={formConfig}
        onClose={handleOnCloseModal}
        isOpen={isModalOpen}
      />
    </div>
  )
}

export default DishesManagement
